import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  FolderOpen,
  AlertCircle,
  RefreshCw,
  Tag,
} from "lucide-react";
import { useBlogStore } from "../store/blogStore";
import { getBlogCategories } from "../lib/supabase";
import BlogSearch from "./BlogSearch";
import Pagination from "./Pagination";
import SEO, { generatePersonSchema } from "./SEO";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";
import OptimizedImage from "./OptimizedImage";

export default function BlogList() {
  const {
    posts,
    totalPosts,
    currentPage,
    loading,
    error,
    selectedTag,
    fetchPosts,
    setPage,
    setSelectedTag,
    clearError,
  } = useBlogStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = React.useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = React.useState(true);
  const [categoriesCache, setCategoriesCache] = React.useState<{
    data: any[];
    timestamp: number;
  } | null>(null);
  const totalPages = Math.ceil(totalPosts / 10);

  useEffect(() => {
    // Check for tag in URL params
    const tagFromUrl = searchParams.get("tag");
    if (tagFromUrl && tagFromUrl !== selectedTag) {
      setSelectedTag(tagFromUrl);
    } else {
      fetchPosts();
    }
  }, [fetchPosts, searchParams, selectedTag, setSelectedTag]);

  useEffect(() => {
    async function loadCategories() {
      try {
        const now = Date.now();
        // Check cache first (5 minute cache)
        if (categoriesCache && now - categoriesCache.timestamp < 300000) {
          setCategories(categoriesCache.data);
          setCategoriesLoading(false);
          return;
        }

        const data = await getBlogCategories();
        setCategories(data);
        setCategoriesCache({ data, timestamp: now });
      } catch (err) {
        console.error("Error loading categories:", err);
      } finally {
        setCategoriesLoading(false);
      }
    }
    loadCategories();
  }, [categoriesCache]);

  const handleTagClick = (tag: string) => {
    setSearchParams({ tag });
    setSelectedTag(tag);
  };

  const clearTagFilter = () => {
    setSearchParams({});
    setSelectedTag(null);
  };

  const handleRetry = () => {
    clearError();
    fetchPosts(currentPage);
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error Loading Blog Posts
            </h3>
            <div className="mt-2">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={handleRetry}
                className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create consolidated schemas for better performance
  const blogPageSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "AI Blog Omaha | One80Labs",
      description:
        "Expert AI blog from Omaha, Nebraska. Learn about AI implementation, project management, and technology innovation from One80Labs in the Midwest.",
      url: "https://one80labs.com/blog",
      mainEntity: {
        "@type": "Blog",
        name: "One80Labs AI Blog",
        description:
          "Expert articles on AI implementation, project management, and technology innovation",
        author: { "@type": "Organization", name: "One80Labs" },
        publisher: {
          "@type": "Organization",
          name: "One80Labs",
          address: {
            "@type": "PostalAddress",
            streetAddress: "4383 Nicholas Street #302",
            addressLocality: "Omaha",
            addressRegion: "NE",
            postalCode: "68181",
            addressCountry: "US",
          },
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://one80labs.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: "https://one80labs.com/blog",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "AI Blog Posts",
      description: "Latest AI and technology blog posts from One80Labs",
      itemListElement: posts.map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: post.title,
        url: `https://one80labs.com/blog/${post.slug}`,
        description: post.excerpt || post.meta_description,
        datePublished: post.published_at || post.created_at,
        author: post.blog_authors?.name || "One80Labs",
      })),
    },
    generatePersonSchema({
      name: "Nick Kramer",
      jobTitle: "AI Consultant & Trainer, Founder, AI Automation Engineer",
      bio: "AI Consultant & Trainer specializing in ChatGPT, Gemini, and Microsoft 365 Copilot training",
      imageUrl:
        "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
      email: "hello@one80labs.com",
      phone: "+1-402-915-3616",
      linkedin: "https://www.linkedin.com/in/nickolaskramer/",
      github: "https://github.com/ndkramer",
      instagram: "https://www.instagram.com/one80labs/",
      education:
        "Degree in Business Administration and Information Systems from Montana State University",
      yearsExperience: {
        ai: 2,
        projectManagement: 15,
        softwareDevelopment: 25,
      },
      expertise: [
        "Artificial Intelligence",
        "Project Management",
        "Product Management",
        "Software Development",
        "ChatGPT",
        "Gemini",
        "Microsoft 365 Copilot",
        "Claude",
        "Automation",
        "Process Improvement",
        "Lean Software Development",
      ],
    }),
  ];

  return (
    <>
      <SEO
        title="AI Blog Omaha | One80Labs - AI Training & Project Management"
        description="Expert AI blog from Omaha, Nebraska. Learn about AI implementation, project management, and technology innovation from One80Labs in the Midwest."
        keywords={[
          "AI blog Omaha",
          "project management blog Nebraska",
          "technology insights Midwest",
          "AI implementation Omaha",
          "business automation Nebraska",
          "digital transformation Midwest",
          "tech education Omaha",
          "AI training Nebraska",
          "fractional PM blog",
          "AI consulting Omaha",
        ]}
        image="https://one80labs.com/logo.png"
        article={false}
        author="One80Labs"
        publishedTime=""
        modifiedTime=""
        section="Blog"
        tags={[
          "AI blog",
          "project management",
          "technology",
          "Omaha",
          "Nebraska",
          "Midwest",
        ]}
        jsonLd={blogPageSchemas}
      />

      <div>
        <h1 className="blog-title" id="main-heading">
          Latest Articles
        </h1>

        {/* Category Navigation */}
        {!categoriesLoading && categories.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/blog/category/${category.slug}`}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors duration-200"
                >
                  <FolderOpen className="h-4 w-4 mr-2" />
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active Tag Filter */}
        {selectedTag && (
          <div className="mb-8">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Filtered by tag:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#F98B3D] text-white">
                {selectedTag}
                <button
                  onClick={clearTagFilter}
                  className="ml-2 hover:text-gray-200"
                >
                  ×
                </button>
              </span>
            </div>
          </div>
        )}

        <BlogSearch />

        {posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No articles found.</p>
            {selectedTag && (
              <button
                onClick={clearTagFilter}
                className="mt-4 text-[#F98B3D] hover:text-[#e07a2c]"
              >
                Clear tag filter
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post) => (
                <article key={post.id} className="blog-card">
                  {post.image_url && (
                    <OptimizedImage
                      src={post.image_url}
                      alt={`Featured image for ${post.title}`}
                      width={400}
                      height={192}
                      loading="lazy"
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="blog-meta mb-2">
                      <time
                        dateTime={post.published_at}
                        className="flex items-center"
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(post.published_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )}
                      </time>
                      <span>•</span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.read_time} min read
                      </span>
                    </div>
                    {post.blog_categories && (
                      <span className="category-tag mb-3">
                        {post.blog_categories.name}
                      </span>
                    )}
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      <Link
                        to={`/blog/${post.slug}`}
                        className="hover:text-[#F98B3D]"
                        aria-label={`Read full article: ${post.title}`}
                      >
                        {post.title}
                      </Link>
                    </h2>
                    <p className="text-gray-600">{post.excerpt}</p>
                    <div className="flex flex-col space-y-3 mt-4 pt-4 border-t border-gray-100">
                      {post.blog_authors && (
                        <div className="flex items-center">
                          {post.blog_authors.image_url && (
                            <OptimizedImage
                              src={post.blog_authors.image_url}
                              alt={`Author ${post.blog_authors.name}`}
                              width={32}
                              height={32}
                              loading="lazy"
                              className="h-8 w-8 rounded-full mr-2"
                            />
                          )}
                          <span className="text-sm text-gray-600">
                            {post.blog_authors.name}
                          </span>
                        </div>
                      )}
                      {post.blog_tag_posts &&
                        post.blog_tag_posts.length > 0 && (
                          <div className="flex items-center flex-wrap gap-2">
                            <Tag className="h-4 w-4 text-gray-400" />
                            {post.blog_tag_posts.map(
                              (tagPost: any) =>
                                tagPost.blog_tags && (
                                  <button
                                    key={tagPost.blog_tags.id}
                                    onClick={() =>
                                      handleTagClick(tagPost.blog_tags.name)
                                    }
                                    className={`text-xs font-medium ${
                                      selectedTag === tagPost.blog_tags.name
                                        ? "text-[#F98B3D]"
                                        : "text-gray-500 hover:text-[#F98B3D]"
                                    } transition-colors duration-200`}
                                  >
                                    #{tagPost.blog_tags.name}
                                  </button>
                                ),
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
